import {
  OPEN_FILTERS_DRAWER,
  CLOSE_FILTERS_DRAWER,
  SET_COMPACT_MODE,
  TOGGLE_COMPACT_MODE,
  SET_THEME_MODE,
  TOGGLE_THEME_MODE,
} from '../actions/actionTypes';
import { ThemeMode, UiAction } from '../actions/uiActions';

const COMPACT_MODE_STORAGE_KEY = 'app_ui_compact_mode';
// Light is the default; only an explicit 'dark' switches the app over.
export const THEME_MODE_STORAGE_KEY = 'app_ui_theme_mode';

function loadCompactMode(): boolean {
  try {
    return localStorage.getItem(COMPACT_MODE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persistCompactMode(value: boolean): void {
  try {
    localStorage.setItem(COMPACT_MODE_STORAGE_KEY, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function loadThemeMode(): ThemeMode {
  try {
    return localStorage.getItem(THEME_MODE_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function persistThemeMode(value: ThemeMode): void {
  try {
    localStorage.setItem(THEME_MODE_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

export interface UiState {
  filtersDrawerOpen: boolean;
  compactMode: boolean;
  themeMode: ThemeMode;
}

// RootState types `ui` as undefined (the CombinedState quirk, CLAUDE.md gotcha #3),
// so read it through this one cast instead of repeating it at every call site.
export const selectIsDarkMode = (s: { ui?: unknown }): boolean =>
  (s.ui as UiState | undefined)?.themeMode === 'dark';

const initialState: UiState = {
  filtersDrawerOpen: false,
  compactMode: loadCompactMode(),
  themeMode: loadThemeMode(),
};

export default function uiReducer(
  state: UiState = initialState,
  action: UiAction,
): UiState {
  switch (action.type) {
    case OPEN_FILTERS_DRAWER:
      return { ...state, filtersDrawerOpen: true };
    case CLOSE_FILTERS_DRAWER:
      return { ...state, filtersDrawerOpen: false };
    case SET_COMPACT_MODE:
      persistCompactMode(action.payload);
      return { ...state, compactMode: action.payload };
    case TOGGLE_COMPACT_MODE: {
      const next = !state.compactMode;
      persistCompactMode(next);
      return { ...state, compactMode: next };
    }
    case SET_THEME_MODE:
      persistThemeMode(action.payload);
      return { ...state, themeMode: action.payload };
    case TOGGLE_THEME_MODE: {
      const next: ThemeMode = state.themeMode === 'dark' ? 'light' : 'dark';
      persistThemeMode(next);
      return { ...state, themeMode: next };
    }
    default:
      return state;
  }
}
